import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileText, Image as ImageIcon, Upload, Trash2, Eye, Loader2, FolderLock, ShieldCheck } from "lucide-react";

const catStyle = { prescription: "bg-amber-100 text-[#92400E]", lab: "bg-teal-100 text-teal-700", other: "bg-slate-100 text-slate-600" };
const catLabel = { prescription: "Prescription", lab: "Lab result", other: "Document" };
const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function RecordCard({ r, onView, onDelete, viewing }) {
  const isPdf = r.content_type === "application/pdf";
  const Icon = isPdf ? FileText : ImageIcon;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isPdf ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500"}`}><Icon className="w-6 h-6" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#041E26] truncate">{r.original_filename}</div>
        <div className="flex items-center gap-2 mt-1">
          <Badge className={catStyle[r.category] || catStyle.other}>{catLabel[r.category] || "Document"}</Badge>
          <span className="text-xs text-slate-400">{fmt(r.created_at)}</span>
        </div>
      </div>
      <button data-testid={`view-record-${r.id}`} onClick={() => onView(r)} disabled={viewing}
        className="w-9 h-9 rounded-full bg-[#041E26] text-white flex items-center justify-center hover:bg-[#0A2F3C] shrink-0">
        {viewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
      </button>
      {onDelete && (
        <button data-testid={`delete-record-${r.id}`} onClick={() => onDelete(r)} className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
      )}
    </motion.div>
  );
}

async function openRecord(r, setViewing) {
  setViewing(r.id);
  try {
    const resp = await api.get(`/records/${r.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Could not open document");
  } finally { setViewing(null); }
}

export function PatientMedicalRecords() {
  const [records, setRecords] = useState([]);
  const [category, setCategory] = useState("prescription");
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => { try { const { data } = await api.get("/records"); setRecords(data.records); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const onUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    setUploading(true);
    try { await api.post("/records/upload", fd); toast.success("Record uploaded securely"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/records/${r.id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <section className="mb-12">
      <div className="flex items-center gap-2 mb-1">
        <FolderLock className="w-5 h-5 text-[#D97706]" />
        <h2 className="font-head text-2xl font-semibold text-[#041E26]">My Medical Records</h2>
      </div>
      <p className="text-slate-500 mb-5 flex items-center gap-1.5 text-sm"><ShieldCheck className="w-4 h-4 text-green-600" /> Private &amp; encrypted — visible only to you and the doctors you consult.</p>

      <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {["prescription", "lab", "other"].map((c) => (
            <button key={c} data-testid={`rec-cat-${c}`} onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${category === c ? "bg-[#041E26] text-white" : "bg-slate-100 text-slate-600"}`}>{catLabel[c]}</button>
          ))}
        </div>
        <label className="ml-auto">
          <input ref={fileRef} data-testid="record-file-input" type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
          <span className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold text-sm cursor-pointer ${uploading ? "bg-slate-200 text-slate-500" : "bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26]"}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload PDF / image
          </span>
        </label>
      </div>

      {records.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">No records yet. Upload your prescriptions and lab results.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {records.map((r) => <RecordCard key={r.id} r={r} viewing={viewing === r.id} onView={(rec) => openRecord(rec, setViewing)} onDelete={del} />)}
        </div>
      )}
    </section>
  );
}

export function PatientRecordsDialog({ patientId, patientName, onClose }) {
  const [records, setRecords] = useState(null);
  const [viewing, setViewing] = useState(null);
  useEffect(() => {
    api.get(`/records/patient/${patientId}`).then(({ data }) => setRecords(data.records)).catch(() => setRecords([]));
  }, [patientId]);
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="patient-records-dialog" className="max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-1"><FolderLock className="w-5 h-5 text-[#D97706]" /><h3 className="font-head text-xl font-semibold text-[#041E26]">{patientName}&apos;s records</h3></div>
        <p className="text-sm text-slate-500 mb-4">Shared prescriptions & lab results for this patient.</p>
        {records === null ? <p className="text-sm text-slate-400">Loading…</p>
          : records.length === 0 ? <div className="text-center text-slate-400 py-8">This patient hasn&apos;t uploaded any records.</div>
          : <div className="space-y-3">{records.map((r) => <RecordCard key={r.id} r={r} viewing={viewing === r.id} onView={(rec) => openRecord(rec, setViewing)} />)}</div>}
      </DialogContent>
    </Dialog>
  );
}
